import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import {
  Background,
  Controls,
  ReactFlow
} from '@xyflow/react';
import ExecutionTraceInspector from './ExecutionTraceInspector';
import ExecutionTraceNode from './ExecutionTraceNode';
import { buildTraceGraph } from './traceLayout';
import './ExecutionTrace.css';


const nodeTypes = { trace: ExecutionTraceNode };

const SOURCE_LABELS = {
  delayed_execution: '延迟执行',
  group_webhook: '规则组 Webhook',
  manual_trigger: '手动触发',
  openapi_trigger: 'OpenAPI 触发',
  query_trigger: '条件查询触发',
  scheduled_query_trigger: '定时查询触发',
  webhook: 'Webhook',
  wecom_mcp_action: '企业微信 Jira MCP'
};

const isTraceRecord = (value) => (
  value !== null
  && typeof value === 'object'
  && !Array.isArray(value)
);

const countValue = (value) => (
  Number.isFinite(value) && value > 0 ? value : 0
);

const summaryValue = (summary, keys) => {
  for (const key of keys) {
    if (Number.isFinite(summary[key])) return countValue(summary[key]);
  }
  return null;
};

const statusTotal = (statuses, names) => (
  names.reduce(
    (total, name) => total + countValue(statuses?.[name]),
    0
  )
);

const executionMetrics = (attempt) => {
  const summary = isTraceRecord(attempt?.summary)
    ? attempt.summary
    : {};
  const ruleStatuses = isTraceRecord(summary.rule_statuses)
    ? summary.rule_statuses
    : {};
  const conditionStatuses = isTraceRecord(summary.condition_statuses)
    ? summary.condition_statuses
    : {};
  const actionStatuses = isTraceRecord(summary.action_statuses)
    ? summary.action_statuses
    : {};
  const explicitFailures = [
    summaryValue(summary, ['conditions_failed']),
    summaryValue(summary, ['actions_failed'])
  ];

  return {
    checked: summaryValue(summary, ['rules_checked', 'rule_count'])
      ?? 0,
    matched: summaryValue(summary, ['rules_matched'])
      ?? statusTotal(ruleStatuses, ['matched']),
    skipped: summaryValue(summary, ['rules_skipped'])
      ?? statusTotal(ruleStatuses, ['skipped']),
    failed: explicitFailures.some((value) => value !== null)
      ? explicitFailures.reduce((total, value) => total + (value ?? 0), 0)
      : (
        statusTotal(conditionStatuses, ['failed', 'error'])
        + statusTotal(actionStatuses, ['failed', 'error'])
      )
  };
};

const attemptLabel = (attempt, index, retryIndex) => {
  if (attempt.source === 'retry') return `重试 ${retryIndex}`;
  if (index === 0) return '首次执行';
  return SOURCE_LABELS[attempt.source]
    || `第 ${index + 1} 次执行`;
};

const ExecutionTracePanel = ({ trace, traceError }) => {
  const attempts = Array.isArray(trace?.attempts)
    ? trace.attempts.filter(isTraceRecord)
    : [];
  const [attemptIndex, setAttemptIndex] = useState(0);
  const attempt = attempts[attemptIndex] || null;
  const graph = useMemo(
    () => buildTraceGraph(attempt || {}),
    [attempt]
  );
  const metrics = useMemo(
    () => executionMetrics(attempt),
    [attempt]
  );
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    setAttemptIndex(0);
    setSelectedNodeId(null);
  }, [trace]);

  useEffect(() => {
    setSelectedNodeId(null);
  }, [attemptIndex]);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const handleFlowInit = useCallback((instance) => {
    const fitOptions = {
      duration: 0,
      maxZoom: 1,
      padding: 0.12
    };
    instance.fitView(fitOptions);
    window.requestAnimationFrame?.(() => {
      instance.fitView(fitOptions);
    });
  }, []);

  const selectedGraphNode = graph.nodes.find(
    (node) => node.id === selectedNodeId
  ) || null;
  const selectedTraceNode = selectedGraphNode?.data.traceNode || null;

  if (traceError) {
    return (
      <div className="execution-trace-state status-error">
        <strong>无法显示判断流程</strong>
        <span>{traceError}</span>
      </div>
    );
  }

  if (!trace) {
    return (
      <div className="execution-trace-state">
        该日志创建时尚未记录判断流程
      </div>
    );
  }

  if (!attempts.length) {
    return (
      <div className="execution-trace-state">
        当前日志尚无可显示的执行尝试
      </div>
    );
  }

  const renderPanel = () => {
    let retryIndex = 0;
    return (
    <div className={`execution-trace-panel${isFullscreen ? ' is-fullscreen' : ''}`}>
      {isFullscreen && (
        <div className="execution-trace-fullscreen-header">
          <div>
            <strong>判断流程</strong>
            <span>全屏工作区</span>
          </div>
          <button
            type="button"
            className="execution-trace-icon-button"
            aria-label="退出全屏判断流程"
            title="退出全屏"
            onClick={() => setIsFullscreen(false)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 3v3a2 2 0 0 1-2 2H3M16 3v3a2 2 0 0 0 2 2h3M8 21v-3a2 2 0 0 0-2-2H3M16 21v-3a2 2 0 0 1 2-2h3" />
            </svg>
          </button>
        </div>
      )}
      <div className="execution-trace-toolbar">
        <div className="execution-trace-attempts" role="tablist">
          {attempts.map((item, index) => {
            if (item.source === 'retry') retryIndex += 1;
            const label = attemptLabel(item, index, retryIndex);
            return (
              <button
                key={`${item.attempt || index}-${item.source}`}
                type="button"
                role="tab"
                aria-selected={index === attemptIndex}
                className={index === attemptIndex ? 'active' : ''}
                onClick={() => setAttemptIndex(index)}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="execution-trace-toolbar-right">
          <div className="execution-trace-meta">
            <div
              className="execution-trace-summary"
              aria-label="执行摘要"
            >
              <span>{SOURCE_LABELS[attempt.source] || attempt.source}</span>
              <span>检查 <strong>{metrics.checked}</strong></span>
              <span>匹配 <strong>{metrics.matched}</strong></span>
              <span>跳过 <strong>{metrics.skipped}</strong></span>
              <span>失败 <strong>{metrics.failed}</strong></span>
              <span>耗时 <strong>{attempt.duration_ms ?? 0} ms</strong></span>
              {attempt.truncated && <span>内容已截断</span>}
            </div>
            <div
              className="execution-trace-legend"
              aria-label="节点状态图例"
            >
              <span><i className="status-passed" />通过 / 匹配 / 成功</span>
              <span><i className="status-skipped" />已跳过</span>
              <span><i className="status-failed" />失败 / 错误</span>
              <span><i className="status-incomplete" />未完成 / 不可用</span>
            </div>
          </div>
          {!isFullscreen && (
            <button
              type="button"
              className="execution-trace-icon-button"
              aria-label="全屏查看判断流程"
              title="全屏查看"
              onClick={() => setIsFullscreen(true)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className={`execution-trace-workspace${selectedTraceNode ? ' has-inspector' : ''}`}>
        <div className="execution-trace-canvas">
          <ReactFlow
            key={`${isFullscreen ? 'fullscreen' : 'inline'}-${attemptIndex}`}
            nodes={graph.nodes.map((node) => ({
              ...node,
              selected: node.id === selectedGraphNode?.id,
              data: {
                ...node.data,
                onSelect: () => setSelectedNodeId(node.id)
              }
            }))}
            edges={graph.edges}
            nodeTypes={nodeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable
            panOnDrag
            zoomOnScroll
            fitView
            fitViewOptions={{ maxZoom: 1, padding: 0.12 }}
            onInit={handleFlowInit}
            minZoom={0.35}
            maxZoom={1.5}
          >
            <Controls showInteractive={false} />
            <Background gap={20} size={1} />
          </ReactFlow>
        </div>
        {selectedTraceNode && (
          <ExecutionTraceInspector
            node={selectedTraceNode}
            onClose={() => setSelectedNodeId(null)}
          />
        )}
      </div>
    </div>
    );
  };

  if (isFullscreen) {
    return createPortal(
      <div className="execution-trace-fullscreen-portal">
        {renderPanel()}
      </div>,
      document.body
    );
  }

  return renderPanel();
};


export default ExecutionTracePanel;
